import React, { useCallback, useEffect, useState } from 'react';
import { fetchPendingInvites, acceptInvite, declineInvite } from './services/orgInviteApi';
import { isImpersonating } from './impersonationView';
import { useAppContext } from '../appprovider';

const wrapperStyle = {
  display: 'flex',
  flexDirection: 'column',
};

const bannerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '12px 20px',
  background: '#eef2ff',
  borderBottom: '1px solid #d6dcfb',
  color: '#2c2a4a',
  fontSize: 14,
};

const actionsStyle = {
  display: 'flex',
  gap: 8,
  flexShrink: 0,
};

const acceptButtonStyle = {
  padding: '6px 14px',
  border: 'none',
  borderRadius: 4,
  background: '#4b2ee0',
  color: '#fff',
  fontWeight: 600,
  cursor: 'pointer',
};

const declineButtonStyle = {
  padding: '6px 14px',
  border: '1px solid #ccc',
  borderRadius: 4,
  background: 'transparent',
  color: '#555',
  cursor: 'pointer',
};

function InviteBanner() {
  const [invites, setInvites] = useState([]);
  const [busyToken, setBusyToken] = useState(null);
  const appContext = useAppContext();

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId || isImpersonating()) {
      return;
    }

    let cancelled = false;

    fetchPendingInvites(userId)
      .then((data) => {
        if (!cancelled && Array.isArray(data)) {
          setInvites(data);
        }
      })
      .catch(() => {
        // Fail silently — no banner when the fetch errors.
      });

    // eslint-disable-next-line consistent-return
    return () => {
      cancelled = true;
    };
  }, []);

  const removeInvite = useCallback((invite) => {
    setInvites((prev) => prev.filter((i) => i.token !== invite.token));
  }, []);

  const handleAccept = useCallback(
    async (invite) => {
      const userId = localStorage.getItem('userId');
      setBusyToken(invite.token);
      try {
        const result = await acceptInvite({ token: invite.token, userId });
        if (result && result.ok) {
          removeInvite(invite);
          if (appContext && typeof appContext.loadEntitlements === 'function') {
            appContext.loadEntitlements(userId);
          } else {
            window.location.reload();
          }
        }
      } catch (err) {
        // Best-effort — leave the invite in the list so the user can retry.
      } finally {
        setBusyToken(null);
      }
    },
    [appContext, removeInvite],
  );

  const handleDecline = useCallback(
    async (invite) => {
      setBusyToken(invite.token);
      try {
        await declineInvite({ token: invite.token });
      } catch (err) {
        // Best-effort — still remove it from view even if the request fails.
      } finally {
        removeInvite(invite);
        setBusyToken(null);
      }
    },
    [removeInvite],
  );

  if (!invites.length) {
    return null;
  }

  return (
    <div style={wrapperStyle}>
      {invites.map((invite) => (
        <div key={invite.inviteId || invite.token} style={bannerStyle}>
          <span>
            <strong>{invite.orgName}</strong> invited you to join as {invite.role}
          </span>
          <div style={actionsStyle}>
            <button
              type="button"
              style={acceptButtonStyle}
              disabled={busyToken === invite.token}
              onClick={() => handleAccept(invite)}
            >
              Accept
            </button>
            <button
              type="button"
              style={declineButtonStyle}
              disabled={busyToken === invite.token}
              onClick={() => handleDecline(invite)}
            >
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default InviteBanner;
